'use client';

import { RefObject } from 'react';
import { Link } from '@/ui-components/atoms/Link/Link';
import { Text } from '@/ui-components/atoms/Text/Text';
import { SnapshotAlertBanner, SnapshotAlertBannerProps } from './SnapshotAlertBanner';
import { SnapshotAlertList } from './SnapshotAlertList';
import { SnapshotPopoverShell } from './SnapshotPopoverShell';
import styles from './snapshotpopover.module.scss';

const SIGN_IN_URL = 'https://care.siriusxm.com/accountcenter/login?';

export interface SnapshotAlertItem {
    ctaHref?: string;
    ctaLabel: string;
    id: string;
    message: string;
}

interface SnapshotAlertPopoverProps {
    alerts?: SnapshotAlertItem[];
    buttonRef: RefObject<HTMLButtonElement | null>;
    featuredAlert?: SnapshotAlertBannerProps;
    open: boolean;
    onClose: () => void;
}

export function SnapshotAlertPopover({
    alerts,
    buttonRef,
    featuredAlert,
    open,
    onClose,
}: SnapshotAlertPopoverProps): React.JSX.Element | null {
    const hasAlerts = !!alerts?.length;
    const hasFeaturedAlert = !!featuredAlert;
    let bodyContent: React.JSX.Element | null = null;

    if (hasFeaturedAlert) {
        bodyContent = (
            <>
                <div className={styles.snapshotDivider}>
                    <div className={styles.snapshotDividerLine} />
                </div>
                <SnapshotAlertBanner {...featuredAlert} />
                {hasAlerts && <SnapshotAlertList alerts={alerts} />}
            </>
        );
    } else if (hasAlerts) {
        bodyContent = <SnapshotAlertList alerts={alerts} />;
    } else {
        bodyContent = (
            <>
                <div className={styles.snapshotDivider}>
                    <div className={styles.snapshotDividerLine} />
                </div>
                <div className={styles.snapshotCenteredBody}>
                    <Text tag="p" className={styles.snapshotCenteredHeading}>
                        Stay in the know
                    </Text>
                    <Text tag="p" className={styles.snapshotCenteredCopy}>
                        <Link className={styles.snapshotAlertInlineLink} href={SIGN_IN_URL}>
                            Sign in
                        </Link>{' '}
                        for personalized reminders
                    </Text>
                </div>
            </>
        );
    }

    return (
        <SnapshotPopoverShell ariaLabel="Your alerts" buttonRef={buttonRef} onClose={onClose} open={open}>
            <div className={styles.snapshotSectionHeader}>
                <Text tag="h3" className={styles.snapshotTitle}>
                    Your alerts
                </Text>
            </div>
            {bodyContent}
        </SnapshotPopoverShell>
    );
}
